import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  DollarSign, 
  LogOut, 
  Menu, 
  X,
  Bell,
  Users,
  UserPlus,
  BarChart3,
  Map,
  StickyNote,
  Truck,
  MessageSquare
} from 'lucide-react';
import { COMPANY_INFO } from '../../data/mock';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [dueReminders, setDueReminders] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    
    if (!token || !adminUser) {
      navigate('/admin/login');
      return;
    }
    
    setAdmin(JSON.parse(adminUser));
  }, [navigate]);

  // Poll for due reminders every 60 seconds
  useEffect(() => {
    let pushSubscriptionRef = null;

    // Register service worker and subscribe to push
    const setupPushNotifications = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // Get VAPID public key from backend
        const token = localStorage.getItem('admin_token');
        const vapidRes = await safeFetch(`${API_URL}/api/admin/notes/vapid-public-key`);
        if (!vapidRes.ok || !vapidRes.data?.publicKey) return;

        const publicKey = vapidRes.data.publicKey;

        // Check existing subscription
        let subscription = await reg.pushManager.getSubscription();
        if (!subscription) {
          // Convert VAPID key to Uint8Array
          const padding = '='.repeat((4 - publicKey.length % 4) % 4);
          const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = atob(base64);
          const keyArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; i++) keyArray[i] = rawData.charCodeAt(i);

          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: keyArray
          });
        }

        pushSubscriptionRef = subscription;

        // Send subscription to backend
        await safeFetch(`${API_URL}/api/admin/notes/push-subscribe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription: subscription.toJSON() })
        });
      } catch (err) {
        console.warn('Push notification setup failed:', err);
      }
    };

    const checkReminders = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) return;
        const result = await safeFetch(`${API_URL}/api/admin/notes/due-reminders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (result.ok) {
          const count = result.data.count || 0;
          setDueReminders(count);
        }
      } catch (err) { /* silent */ }
    };

    // Request notification permission first, then setup push
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(perm => {
        if (perm === 'granted') setupPushNotifications();
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      setupPushNotifications();
    }

    checkReminders();
    const interval = setInterval(checkReminders, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/admin/map-dispatch', icon: Map, label: 'Map Dispatch' },
    { path: '/admin/leads', icon: UserPlus, label: 'Leads' },
    { path: '/admin/clients', icon: Users, label: 'Clients' },
    { path: '/admin/pricing', icon: DollarSign, label: 'Pricing' },
    { path: '/admin/drivers', icon: Truck, label: 'Drivers' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/notes', icon: StickyNote, label: 'Notes' },
    { path: '/admin/sms-templates', icon: MessageSquare, label: 'SMS Templates' },
  ];

  const isActive = (path) => location.pathname === path;

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-[#E2E8F0] shadow-sm
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#E2E8F0]">
          <img 
            src={COMPANY_INFO.logoUrl} 
            alt="Transa" 
            className="h-8"
          />
          <button 
            className="lg:hidden text-[#64748B] hover:text-[#111111]"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                ${isActive(item.path) 
                  ? 'bg-[#0D9488] text-white' 
                  : 'text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#111111]'
                }
              `}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {item.label === 'Notes' && dueReminders > 0 && (
                <span className="ml-auto min-w-[20px] h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold px-1.5">
                  {dueReminders}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E2E8F0]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#64748B] hover:bg-[#FEF2F2] hover:text-red-600 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button 
            className="lg:hidden text-[#64748B] hover:text-[#111111]"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <button 
              className="text-[#64748B] hover:text-[#111111] relative"
              onClick={() => navigate('/admin/notes')}
              data-testid="reminder-bell"
            >
              <Bell className="w-5 h-5" />
              {dueReminders > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1">
                  {dueReminders}
                </span>
              )}
            </button>

            <div className="flex items-center gap-3 pl-4 border-l border-[#E2E8F0]">
              <div className="w-9 h-9 rounded-full bg-[#0D9488] flex items-center justify-center text-white font-medium">
                {admin.name?.charAt(0) || 'A'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-[#111111]">{admin.name}</p>
                <p className="text-xs text-[#64748B] capitalize">{admin.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
